import { useState } from "react";
import { Check, X, RotateCcw, ArrowLeft } from "lucide-react";
import { QUESTIONS, scoreAnswers } from "@/lib/riskQuiz";
import { SectionHead } from "@/components/site/SectionHead";
import { RiskResult } from "@/components/site/RiskResult";

const QuizCard = ({ idx, onAnswer, onBack }) => {
  const q = QUESTIONS[idx];
  const pct = Math.round((idx / QUESTIONS.length) * 100);
  return (
    <div key={q.id} className="btr-page-enter" data-testid="risk-quiz">
      <div className="flex items-center justify-between text-[11px] font-display tracking-[0.25em] uppercase text-zinc-500">
        <span data-testid="risk-progress-label">Question {idx + 1} of {QUESTIONS.length}</span>
        {idx > 0 && (
          <button onClick={onBack} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="risk-back-btn">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        )}
      </div>
      <div className="h-1 bg-white/5 rounded-full mt-3 overflow-hidden">
        <div className="h-full bg-[#a2020b] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="font-display font-bold text-white text-xl sm:text-2xl mt-8 leading-snug min-h-[96px]" data-testid="risk-question">
        {q.q}
      </p>
      <div className="grid grid-cols-2 gap-3 mt-8">
        <button onClick={() => onAnswer(true)} className="btr-btn-primary justify-center" data-testid="risk-answer-yes">
          <Check className="w-4 h-4" /> Yes
        </button>
        <button onClick={() => onAnswer(false)} className="btr-btn-ghost justify-center" data-testid="risk-answer-no">
          <X className="w-4 h-4" /> No
        </button>
      </div>
    </div>
  );
};

export const RiskReadiness = () => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = idx >= QUESTIONS.length;

  const answer = (val) => {
    setAnswers((a) => ({ ...a, [QUESTIONS[idx].id]: val }));
    setIdx((i) => i + 1);
  };
  const reset = () => {
    setAnswers({});
    setIdx(0);
  };

  return (
    <section id="risk-score" className="relative py-20 sm:py-24 md:py-32 overflow-hidden btr-bg-graphite" data-testid="risk-readiness-section">
      <div className="absolute inset-0 btr-grid opacity-20 pointer-events-none" />
      <div className="btr-beam" style={{ width: 520, height: 520, top: "10%", left: "-12%", background: "#a2020b", opacity: 0.16 }} />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start">
        <div>
          <SectionHead
            badge="Risk Readiness Score"
            title={<>How prepared are you <span className="text-[#a2020b]">before</span> the risk?</>}
            lede="Six quick questions. An instant, preliminary readiness score — no email required. Discover where your property, records, and response plan may be exposed."
          />
          <ul className="btr-reveal mt-8 space-y-3 font-body text-sm text-zinc-400">
            {["Takes under 60 seconds", "Instant score and risk areas", "Optional personalized snapshot"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#a2020b] shadow-[0_0_8px_#a2020b]" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="btr-reveal btr-glass rounded-2xl p-6 sm:p-9 relative overflow-hidden">
          <div className="absolute top-0 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-[#a2020b]/60 to-transparent" />
          {done ? (
            <RiskResult result={scoreAnswers(answers)} onReset={reset} />
          ) : (
            <QuizCard idx={idx} onAnswer={answer} onBack={() => setIdx((i) => Math.max(0, i - 1))} />
          )}
          {done && (
            <button onClick={reset} className="mt-6 inline-flex items-center gap-1.5 text-[11px] font-display tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors" data-testid="risk-retake-btn">
              <RotateCcw className="w-3 h-3" /> Retake assessment
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
